import { useId, useState, type ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { Icon } from "../shell/icons.js";

/**
 * حقل كلمة السرّ — **المكوّن المشترك الوحيد، ولا حقل مباشر في أي مكان**.
 *
 * وفيه زرّ إظهار: الكتابة بلا رؤية على هاتفٍ بلوحة مفاتيح عربية سببُ نصف
 * محاولات الدخول الفاشلة، وكل محاولة فاشلة تقترب بصاحبها من حدّ المعدّل.
 *
 * والزرّ يقول حالته للقارئ الصوتي، لا بالأيقونة وحدها.
 */
export function PasswordField({
  value,
  onChange,
  label,
  autoComplete = "current-password",
  invalid = false,
  describedBy,
  required = false,
  minLength,
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly label: string;
  readonly autoComplete?: "current-password" | "new-password";
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly required?: boolean;
  /**
   * **أقلُّ طولٍ — من سياسة القلب، لا رقماً مكتوباً هنا.**
   *
   * فالمتصفّحُ يمنع الإرسالَ قبل الرحلة، **والسيرفرُ يفحص على كل حال**: فحصُ
   * الواجهة راحةٌ لا حماية، ولو كان وحده لمرَّ كلُّ طلبٍ لا يأتي من الشاشة.
   */
  readonly minLength?: number;
}): ReactNode {
  const { t } = useLocale();
  const [visible, setVisible] = useState(false);
  const id = useId();

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="pw">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          required={required || undefined}
          minLength={minLength}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <button
          type="button"
          className="eye"
          aria-pressed={visible}
          aria-label={t(visible ? "common.hidePassword" : "common.showPassword")}
          onClick={() => {
            setVisible((shown) => !shown);
          }}
        >
          <Icon name={visible ? "eye-off" : "eye"} />
        </button>
      </div>
    </div>
  );
}
